/**
 * Unified Chatbot Hook
 * 
 * Single hook for all chatbot data operations (queries and mutations).
 * Replaces useChatbotBuilder, useChatbotQueries, and useChatbotMutations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { validateChatbotId } from '@/utils/chatbotUtils';
import { transformThemeData, prepareThemeForDatabase } from '@/utils/themeUtils';
import { transformQuestions, transformTriggerRules } from '@/utils/chatbotTransformUtils';
import {
  fetchChatbotFullData,
  fetchChatbot,
  fetchTheme,
  fetchQuestions,
  fetchTriggerRules,
  fetchActiveChatbot
} from '@/services/chatbotDataService';
import {
  updateChatbotData,
  deleteChatbot,
  uploadChatbotAvatar,
  uploadOperatorAvatar,
  updateTheme,
  upsertTheme,
  addQuestion,
  updateQuestion,
  deleteQuestion,
  updateQuestionsOrder,
  addTriggerRule,
  updateTriggerRule,
  deleteTriggerRule
} from '@/services/chatbotMutationsService';
import { saveChatbot } from '@/services/chatbotSaveService';
import { UnifiedChatbotData, UnifiedChatbotSettings, DEFAULT_UNIFIED_SETTINGS } from '@/types/builder';
import { ChatTheme, Question, TriggerRule } from '@/types/theme';
import { parseSettings } from '@/utils/settingsUtils';

/**
 * Main hook for chatbot data operations
 */
export function useChatbot(chatbotId: string | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const validChatbotId = validateChatbotId(chatbotId);

  // Unified query for all chatbot data
  const {
    data: rawData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['chatbot-full', validChatbotId],
    queryFn: async () => {
      if (!validChatbotId) {
        throw new Error('Invalid chatbot ID format');
      }
      return fetchChatbotFullData(validChatbotId);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.message?.includes('not found')) {
        return false;
      }
      return failureCount < 2;
    }
  });

  // Transform data
  const chatbot = rawData?.chatbot;
  const theme = rawData?.theme ? transformThemeData(rawData.theme) : null;
  const questions = rawData?.questions ? transformQuestions(rawData.questions) : [];
  const triggerRules = rawData?.triggerRules ? transformTriggerRules(rawData.triggerRules) : [];

  // Parse settings
  const settings = parseSettings(chatbot?.settings) as UnifiedChatbotSettings;

  // Unified data object
  const unifiedData: UnifiedChatbotData | undefined = chatbot ? {
    ...chatbot,
    theme: theme || undefined,
    questions,
    triggerRules,
    settings: settings || DEFAULT_UNIFIED_SETTINGS
  } : undefined;

  // Save mutation using centralized service
  const saveMutation = useMutation({
    mutationFn: async (updates: Partial<UnifiedChatbotData>) => {
      if (!validChatbotId) {
        throw new Error('Invalid chatbot ID');
      }

      const result = await saveChatbot(validChatbotId, updates, {
        mergeSettings: true,
        preserveAiSettings: true
      });

      if (!result.success || result.error) {
        throw result.error || new Error('Failed to save chatbot');
      }

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-full', validChatbotId] });
      queryClient.invalidateQueries({ queryKey: ['chatbot-builder', validChatbotId] });
      queryClient.invalidateQueries({ queryKey: ['chatbot', validChatbotId] });
      toast({
        title: 'Settings saved',
        description: 'Your changes have been saved successfully.'
      });
    },
    onError: (error: any) => {
      console.error('Save error:', error);
      toast({
        title: 'Error saving changes',
        description: error.message || 'Please try again',
        variant: 'destructive'
      });
    }
  });

  // Theme save mutation
  const saveThemeMutation = useMutation({
    mutationFn: async (theme: Partial<ChatTheme>) => {
      if (!validChatbotId) {
        throw new Error('Invalid chatbot ID');
      }
      return upsertTheme(validChatbotId, theme);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-full', validChatbotId] });
      queryClient.invalidateQueries({ queryKey: ['theme', validChatbotId] });
      toast({
        title: 'Theme saved',
        description: 'Your theme has been saved successfully.'
      });
    },
    onError: (error: any) => {
      console.error('Theme save error:', error);
      toast({
        title: 'Error saving theme',
        description: error.message || 'Please try again',
        variant: 'destructive'
      });
    }
  });

  // Question mutations
  const addQuestionMutation = useMutation({
    mutationFn: async (question: Omit<Question, 'id' | 'chatbot_id'>) => {
      if (!validChatbotId) throw new Error('Invalid chatbot ID');
      return addQuestion(validChatbotId, question);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-full', validChatbotId] });
      queryClient.invalidateQueries({ queryKey: ['questions', validChatbotId] });
      toast({
        title: 'Question added',
        description: 'New question has been added successfully.'
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message
      });
    }
  });

  const updateQuestionMutation = useMutation({
    mutationFn: async ({ questionId, updates }: { questionId: string; updates: Partial<Question> }) => {
      if (!validChatbotId) throw new Error('Invalid chatbot ID');
      return updateQuestion(validChatbotId, questionId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-full', validChatbotId] });
      queryClient.invalidateQueries({ queryKey: ['questions', validChatbotId] });
      toast({
        title: 'Question updated',
        description: 'Question has been updated successfully.'
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message
      });
    }
  });

  const deleteQuestionMutation = useMutation({
    mutationFn: async (questionId: string) => {
      if (!validChatbotId) throw new Error('Invalid chatbot ID');
      return deleteQuestion(validChatbotId, questionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-full', validChatbotId] });
      queryClient.invalidateQueries({ queryKey: ['questions', validChatbotId] });
      toast({
        title: 'Question deleted',
        description: 'Question has been deleted successfully.'
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message
      });
    }
  });

  const updateQuestionsOrderMutation = useMutation({
    mutationFn: async (questions: Question[]) => {
      if (!validChatbotId) throw new Error('Invalid chatbot ID');
      return updateQuestionsOrder(validChatbotId, questions);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-full', validChatbotId] });
      queryClient.invalidateQueries({ queryKey: ['questions', validChatbotId] });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Error updating question order',
        description: error.message || 'Failed to save the new question order'
      });
    }
  });

  // Trigger rule mutations
  const addTriggerRuleMutation = useMutation({
    mutationFn: async () => {
      if (!validChatbotId) throw new Error('Invalid chatbot ID');
      return addTriggerRule(validChatbotId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-full', validChatbotId] });
      queryClient.invalidateQueries({ queryKey: ['trigger_rules', validChatbotId] });
      toast({
        title: 'Trigger rule added',
        description: 'New trigger rule has been added successfully.'
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message
      });
    }
  });

  const updateTriggerRuleMutation = useMutation({
    mutationFn: async ({ ruleId, updates }: { ruleId: string; updates: Partial<TriggerRule> }) => {
      if (!validChatbotId) throw new Error('Invalid chatbot ID');
      return updateTriggerRule(validChatbotId, ruleId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-full', validChatbotId] });
      queryClient.invalidateQueries({ queryKey: ['trigger_rules', validChatbotId] });
      toast({
        title: 'Trigger rule updated',
        description: 'Trigger rule has been updated successfully.'
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message
      });
    }
  });

  const deleteTriggerRuleMutation = useMutation({
    mutationFn: async (ruleId: string) => {
      if (!validChatbotId) throw new Error('Invalid chatbot ID');
      return deleteTriggerRule(validChatbotId, ruleId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-full', validChatbotId] });
      queryClient.invalidateQueries({ queryKey: ['trigger_rules', validChatbotId] });
      toast({
        title: 'Trigger rule deleted',
        description: 'Trigger rule has been deleted successfully.'
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message
      });
    }
  });

  // Avatar mutations
  const uploadAvatarMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!validChatbotId) throw new Error('Invalid chatbot ID');
      return uploadChatbotAvatar(validChatbotId, file);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-full', validChatbotId] });
      queryClient.invalidateQueries({ queryKey: ['chatbot', validChatbotId] });
      toast({
        title: 'Avatar uploaded',
        description: 'Your chatbot avatar has been updated successfully.'
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Error uploading avatar',
        description: error.message
      });
    }
  });

  const uploadOperatorAvatarMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!validChatbotId) throw new Error('Invalid chatbot ID');
      return uploadOperatorAvatar(validChatbotId, file);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-full', validChatbotId] });
      queryClient.invalidateQueries({ queryKey: ['chatbot', validChatbotId] });
      toast({
        title: 'Operator avatar uploaded',
        description: 'The operator avatar has been updated successfully.'
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Error uploading operator avatar',
        description: error.message
      });
    }
  });

  const uploadCompanyLogoMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!validChatbotId) throw new Error('Invalid chatbot ID');
      return uploadCompanyLogo(validChatbotId, file);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-full', validChatbotId] });
      queryClient.invalidateQueries({ queryKey: ['chatbot', validChatbotId] });
      toast({
        title: 'Operator avatar uploaded',
        description: 'The operator avatar has been updated successfully.'
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Error uploading operator avatar',
        description: error.message
      });
    }
  });

  // Delete chatbot mutation
  const deleteChatbotMutation = useMutation({
    mutationFn: async () => {
      if (!validChatbotId) throw new Error('Invalid chatbot ID');
      return deleteChatbot(validChatbotId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbots'] });
      toast({
        title: 'Chatbot deleted',
        description: 'The chatbot has been successfully deleted.'
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Error deleting chatbot',
        description: error.message
      });
    }
  });

  return {
    // Data
    data: unifiedData,
    chatbot,
    theme,
    questions,
    triggerRules,
    settings,
    
    // Loading states
    isLoading,
    isSaving: saveMutation.isPending,
    
    // Error
    error: error?.message || null,
    
    // Refetch
    refetch,
    
    // Mutations
    save: saveMutation.mutateAsync,
    saveTheme: saveThemeMutation.mutateAsync,
    addQuestion: addQuestionMutation.mutateAsync,
    updateQuestion: updateQuestionMutation.mutateAsync,
    deleteQuestion: deleteQuestionMutation.mutateAsync,
    updateQuestionsOrder: updateQuestionsOrderMutation.mutateAsync,
    addTriggerRule: addTriggerRuleMutation.mutateAsync,
    updateTriggerRule: updateTriggerRuleMutation.mutateAsync,
    deleteTriggerRule: deleteTriggerRuleMutation.mutateAsync,
    uploadAvatar: uploadAvatarMutation.mutateAsync,
    uploadOperatorAvatar: uploadOperatorAvatarMutation.mutateAsync,
    deleteChatbot: deleteChatbotMutation.mutateAsync,
    
    // Mutation states
    mutations: {
      save: saveMutation,
      saveTheme: saveThemeMutation,
      addQuestion: addQuestionMutation,
      updateQuestion: updateQuestionMutation,
      deleteQuestion: deleteQuestionMutation,
      updateQuestionsOrder: updateQuestionsOrderMutation,
      addTriggerRule: addTriggerRuleMutation,
      updateTriggerRule: updateTriggerRuleMutation,
      deleteTriggerRule: deleteTriggerRuleMutation,
      uploadAvatar: uploadAvatarMutation,
      uploadOperatorAvatar: uploadOperatorAvatarMutation,
      deleteChatbot: deleteChatbotMutation
    }
  };
}




